import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Briefcase, Users, Video, LayoutDashboard, BarChart3, Settings,
  ChevronRight, ChevronLeft, CheckCircle2, BookOpen, MapPin, Clock,
  FileText, Sparkles, Play, Share2, Send, Award, ListChecks, PlusCircle,
  CircleDot, ArrowRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';

interface GuideItem {
  key: string;
  icon?: React.ElementType;
  hasDetail?: boolean;
}

interface GuideSection {
  key: string;
  items: GuideItem[];
}

interface GuideStep {
  id: string;
  icon: React.ElementType;
  sections: GuideSection[];
}

const guideSteps: GuideStep[] = [
  {
    id: 'welcome',
    icon: BookOpen,
    sections: [
      {
        key: 'learn',
        items: [
          { key: 'creatingJobs', icon: Briefcase, hasDetail: true },
          { key: 'trackingCandidates', icon: Users, hasDetail: true },
          { key: 'aiInterviews', icon: Video, hasDetail: true },
          { key: 'dashboardAnalytics', icon: LayoutDashboard, hasDetail: true },
        ],
      },
    ],
  },
  {
    id: 'dashboard',
    icon: LayoutDashboard,
    sections: [
      {
        key: 'metrics',
        items: [
          { key: 'activeJobs', icon: CircleDot, hasDetail: true },
          { key: 'totalCandidates', icon: Users, hasDetail: true },
          { key: 'interviewsCompleted', icon: Video, hasDetail: true },
          { key: 'averageCvScore', icon: Award, hasDetail: true },
        ],
      },
      {
        key: 'quickActions',
        items: [
          { key: 'createJob' },
          { key: 'recentApplications' },
          { key: 'pipelineHealth' },
        ],
      },
    ],
  },
  {
    id: 'jobs',
    icon: Briefcase,
    sections: [
      {
        key: 'creating',
        items: [
          { key: 'clickCreateJob', icon: PlusCircle, hasDetail: true },
          { key: 'titleDepartment', icon: FileText, hasDetail: true },
          { key: 'locationType', icon: MapPin, hasDetail: true },
          { key: 'requirements', icon: ListChecks, hasDetail: true },
          { key: 'duration', icon: Clock, hasDetail: true },
        ],
      },
      {
        key: 'managing',
        items: [
          { key: 'activate', icon: Play, hasDetail: true },
          { key: 'share', icon: Share2, hasDetail: true },
          { key: 'edit' },
          { key: 'close' },
        ],
      },
      {
        key: 'detailPage',
        items: [
          { key: 'pipelineView' },
          { key: 'candidatesTable' },
          { key: 'aboutRole' },
        ],
      },
    ],
  },
  {
    id: 'candidates',
    icon: Users,
    sections: [
      {
        key: 'entry',
        items: [
          { key: 'publicLink' },
          { key: 'detailsCv' },
          { key: 'duplicates' },
        ],
      },
      {
        key: 'profile',
        items: [
          { key: 'cvPreview', icon: FileText, hasDetail: true },
          { key: 'aiCvScore', icon: Sparkles, hasDetail: true },
          { key: 'interviewResults', icon: Video, hasDetail: true },
        ],
      },
      {
        key: 'pipeline',
        items: [
          { key: 'advance', icon: ArrowRight, hasDetail: true },
          { key: 'rollBack' },
          { key: 'reject' },
          { key: 'audit' },
        ],
      },
    ],
  },
  {
    id: 'interviews',
    icon: Video,
    sections: [
      {
        key: 'setup',
        items: [
          { key: 'jobInterviewTab', icon: PlusCircle, hasDetail: true },
          { key: 'addQuestions', icon: ListChecks, hasDetail: true },
          { key: 'requiredFields' },
          { key: 'publish' },
        ],
      },
      {
        key: 'inviting',
        items: [
          { key: 'sendLink', icon: Send, hasDetail: true },
          { key: 'expiry' },
          { key: 'retry' },
        ],
      },
      {
        key: 'scoring',
        items: [
          { key: 'candidatesTab' },
          { key: 'aiScored', icon: Sparkles, hasDetail: true },
          { key: 'expandRow' },
        ],
      },
    ],
  },
  {
    id: 'analytics',
    icon: BarChart3,
    sections: [
      {
        key: 'analytics',
        items: [
          { key: 'pipelineFunnel' },
          { key: 'hiringVelocity' },
          { key: 'scoreDistributions' },
        ],
      },
      {
        key: 'settings',
        items: [
          { key: 'organization', icon: Settings, hasDetail: true },
          { key: 'team' },
          { key: 'templates' },
        ],
      },
    ],
  },
];

export function OnboardingGuide() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const seen = localStorage.getItem('forkhire_onboarding_seen');
    if (!seen) {
      const timer = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const handler = () => { setCurrentStep(0); setOpen(true); };
    window.addEventListener('open-onboarding', handler);
    return () => window.removeEventListener('open-onboarding', handler);
  }, []);

  const handleClose = () => {
    const isFirstTime = !localStorage.getItem('forkhire_onboarding_seen');
    setOpen(false);
    localStorage.setItem('forkhire_onboarding_seen', 'true');
    if (isFirstTime) {
      setTimeout(() => {
        toast({
          title: t('hire.onboardingGuide.helpToast.title'),
          description: t('hire.onboardingGuide.helpToast.description'),
          duration: 8000,
        });
      }, 500);
    }
  };

  const handleNext = () => {
    if (currentStep < guideSteps.length - 1) setCurrentStep(currentStep + 1);
    else handleClose();
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const step = guideSteps[currentStep];
  const StepIcon = step.icon;
  const isLast = currentStep === guideSteps.length - 1;

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); else { setCurrentStep(0); setOpen(true); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] p-0 gap-0 rounded-2xl overflow-hidden">
          <div className="h-1 bg-muted">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${((currentStep + 1) / guideSteps.length) * 100}%` }}
            />
          </div>

          <DialogHeader className="px-6 pt-5 pb-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <StepIcon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="font-display text-lg">{t(`hire.onboardingGuide.steps.${step.id}.title`)}</DialogTitle>
                <DialogDescription className="text-sm mt-0.5">{t(`hire.onboardingGuide.steps.${step.id}.subtitle`)}</DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <ScrollArea className="px-6 py-4 max-h-[55vh]">
            <div className="space-y-5">
              {step.sections.map((section) => (
                <div key={section.key}>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    {t(`hire.onboardingGuide.steps.${step.id}.sections.${section.key}.heading`)}
                  </h4>
                  <div className="space-y-2">
                    {section.items.map((item) => {
                      const ItemIcon = item.icon;
                      const itemKey = `hire.onboardingGuide.steps.${step.id}.sections.${section.key}.items.${item.key}`;
                      return (
                        <div
                          key={item.key}
                          className="flex gap-3 p-3 rounded-xl bg-muted/50 hover:bg-muted/80 transition-colors"
                        >
                          {ItemIcon && (
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <ItemIcon className="w-4 h-4 text-primary" />
                            </div>
                          )}
                          {!ItemIcon && (
                            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <div className="w-1.5 h-1.5 rounded-full bg-primary/60" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground">{t(`${itemKey}.text`)}</p>
                            {item.hasDetail && (
                              <p className="text-xs text-muted-foreground mt-0.5">{t(`${itemKey}.detail`)}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>

          <DialogFooter className="px-6 py-4 border-t border-border bg-muted/30">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-1.5">
                {guideSteps.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentStep(i)}
                    className={cn(
                      'w-2 h-2 rounded-full transition-all duration-200',
                      i === currentStep ? 'w-6 bg-primary' : i < currentStep ? 'bg-primary/40' : 'bg-muted-foreground/20'
                    )}
                    aria-label={t('hire.onboardingGuide.stepIndicator', { step: i + 1 })}
                  />
                ))}
              </div>

              <div className="flex items-center gap-2">
                {currentStep > 0 && (
                  <Button variant="ghost" size="sm" onClick={handlePrev} className="h-9">
                    <ChevronLeft className="w-4 h-4 me-1 rtl-flip" /> {t('hire.onboardingGuide.back')}
                  </Button>
                )}
                {currentStep === 0 && (
                  <Button variant="ghost" size="sm" onClick={handleClose} className="h-9 text-muted-foreground">
                    {t('hire.onboardingGuide.skip')}
                  </Button>
                )}
                <Button size="sm" onClick={handleNext} className="h-9 px-4">
                  {isLast ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 me-1" /> {t('hire.onboardingGuide.getStarted')}
                    </>
                  ) : (
                    <>
                      {t('hire.onboardingGuide.next')} <ChevronRight className="w-4 h-4 ms-1 rtl-flip" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function OnboardingTrigger({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-sidebar-foreground/50 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-all w-full touch-target"
    >
      <BookOpen className="w-5 h-5 flex-shrink-0" />
      <span>{t('hire.onboardingGuide.triggerLabel')}</span>
    </button>
  );
}
